import React, { useState, useEffect } from 'react';
import { CreditService } from '../services/creditService';

interface CreditInfo {
  remaining: number;
  total: number;
  used: number;
  currency: string;
  lastUpdated: string;
}

export const CreditDisplay: React.FC = () => {
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Load credit info on mount and set up auto-refresh
  useEffect(() => {
    const loadCreditInfo = async () => {
      try {
        setLoading(true);
        const info = await CreditService.getCreditInfo();
        setCreditInfo(info);
      } catch (error) {
        console.error('Failed to load credit info:', error);
      } finally {
        setLoading(false);
      }
    };

    // Initial load
    loadCreditInfo();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(loadCreditInfo, 30000);

    // Listen for manual credit update events (after video generation)
    const handleCreditUpdate = () => {
      loadCreditInfo();
    };
    window.addEventListener('credit-update', handleCreditUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('credit-update', handleCreditUpdate);
    };
  }, []);

  // Calculate percentage
  const getPercentage = () => {
    if (!creditInfo) return 0;
    return ((creditInfo.remaining / creditInfo.total) * 100).toFixed(1);
  };

  // Get color based on remaining credit
  const getColor = () => {
    if (!creditInfo) return 'text-gray-500';
    const percentage = parseFloat(getPercentage());
    if (percentage > 50) return 'text-green-500';
    if (percentage > 20) return 'text-yellow-500';
    return 'text-red-500';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: creditInfo?.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  if (loading && !creditInfo) {
    return (
      <div className="fixed top-4 right-4 bg-slate-800 rounded-lg p-3 shadow-lg">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-600 rounded-full"></div>
          <div className="h-4 w-24 bg-slate-600 rounded"></div>
        </div>
      </div>
    );
  }

  if (!creditInfo) {
    return null;
  }

  return (
    <div 
      className="fixed right-4 z-[9999] bg-slate-800 rounded-lg p-4 shadow-lg border border-slate-700 min-w-[250px]"
      style={{ top: "calc(var(--app-header-h, 56px) + env(safe-area-inset-top, 0px) + 12px)" }}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-300">Google Cloud Credit</h3>
        <div className="relative group">
          <svg 
            className="w-4 h-4 text-slate-400 cursor-help" 
            fill="none" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <div className="absolute right-0 top-6 w-48 p-2 bg-slate-700 rounded text-xs text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Google Cloud credits for video generation only. Script generation uses FREE Gemini API.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {/* Credit Bar */}
        <div className="relative">
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Remaining</span>
            <span>{getPercentage()}%</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${
                parseFloat(getPercentage()) > 50 ? 'bg-green-500' :
                parseFloat(getPercentage()) > 20 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${getPercentage()}%` }}
            />
          </div>
        </div>

        {/* Credit Details */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Remaining:</span>
            <p className={`font-mono font-semibold ${getColor()}`}>
              {formatCurrency(creditInfo.remaining)}
            </p>
          </div>
          <div>
            <span className="text-slate-400">Used:</span>
            <p className="font-mono text-slate-300">
              {formatCurrency(creditInfo.used)}
            </p>
          </div>
        </div>

        {/* Total Credit */}
        <div className="pt-2 border-t border-slate-700">
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Total Credit:</span>
            <span className="font-mono text-slate-300">
              {formatCurrency(creditInfo.total)}
            </span>
          </div>
        </div>

        {/* Cost Breakdown (Optional) */}
        <details className="text-xs text-slate-400 cursor-pointer">
          <summary className="hover:text-slate-300 transition-colors">
            API Usage Info
          </summary>
          <div className="mt-2 space-y-1 pl-2">
            <div className="flex justify-between">
              <span>Script (Gemini):</span>
              <span className="font-mono text-green-400">FREE</span>
            </div>
            <div className="flex justify-between">
              <span>Video (Google Cloud):</span>
              <span className="font-mono">$0.002/second</span>
            </div>
            <div className="flex justify-between mt-2 pt-2 border-t border-slate-600">
              <span>8초 영상 비용:</span>
              <span className="font-mono text-yellow-400">$0.016</span>
            </div>
          </div>
        </details>

        {/* Last Updated */}
        <div className="text-xs text-slate-500 text-center pt-2">
          Updated: {new Date(creditInfo.lastUpdated).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};